import { IconButton, Typography } from '@sweep-io/sweep-design';
import { Stack } from '@mui/material';
import { AgentSettingsDialogLayout } from '../../../aiAgentCenter/agentSettings/AgentSettingsDialogLayout';
import { SectionMenuItem } from '../../../aiAgentCenter/agentSettings/SectionMenuItem';
import { useState } from 'react';
import { Close, Apps, Wrench } from '@sweep-io/sweep-design/dist/icons';
import { AppsConnection } from '../apps/AppsConnection';
import { InstructionsContent } from '../instructions/InstructionsContent';
import { usePristineCard } from '../instructions/usePristineCard';
import { ConfirmLeaveWithoutSaveDialog } from '../../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';

enum Sections {
  SalesforceApps = 'salesforce-apps',
  Instructions = 'instructions',
}

const menu = {
  [Sections.SalesforceApps]: {
    label: 'Salesforce apps',
    icon: <Apps />,
  },
  [Sections.Instructions]: {
    label: 'Custom Instructions',
    icon: <Wrench />,
  },
};

export const AiAssistantDialogWithMenu = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: (e: any) => void;
}) => {
  const [selectedSection, setSelectedSection] = useState<Sections>(Sections.SalesforceApps);
  const { openConfirm } = useConfirm();
  const {
    isPristine,
    setEditableCardPristineStates,
    isCreatingNew,
    setIsCreatingNew,
    setNewCardIsPristine,
  } = usePristineCard();

  const handleTryCloseDialog = async (e: any) => {
    if (!isPristine) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) return;
    }
    onClose(e);
  };

  const renderDialogContent = () => {
    switch (selectedSection) {
      case Sections.SalesforceApps:
        return (
          <Stack sx={{ height: '100%' }}>
            <Stack p={2} pb={2} justifyContent="space-between" alignItems="center" direction="row">
              <Typography variant="h3">Salesforce Apps</Typography>
              <IconButton variant="flat" size="tiny" onClick={onClose}>
                <Close />
              </IconButton>
            </Stack>
            <AppsConnection
              closeDialog={onClose}
              containerSx={{ maxHeight: 'calc(100% - 100px)', height: '100%' }}
            />
          </Stack>
        );
      case Sections.Instructions:
        return (
          <InstructionsContent
            onClose={handleTryCloseDialog}
            setEditableCardPristineStates={setEditableCardPristineStates}
            setIsCreatingNew={setIsCreatingNew}
            isCreatingNew={isCreatingNew}
            setNewCardIsPristine={setNewCardIsPristine}
          />
        );
    }
  };

  const renderMenu = () => {
    return Object.keys(menu).map((key) => (
      <SectionMenuItem
        key={key}
        isSelected={selectedSection === key}
        isDisabled={false}
        onClick={async () => {
          if (!isPristine) {
            const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
            if (!isConfirmed) return;
          }
          setSelectedSection(key as Sections);
        }}
      >
        <Stack direction="row" gap={1} alignItems="center">
          {menu[key as keyof typeof menu].icon}
          <Typography variant="body"> {menu[key as keyof typeof menu].label}</Typography>
        </Stack>
      </SectionMenuItem>
    ));
  };

  return (
    <AgentSettingsDialogLayout
      isOpen={isOpen}
      onClose={handleTryCloseDialog}
      menuJsx={renderMenu()}
      children={renderDialogContent()}
    />
  );
};
